import { useEffect, useMemo, useRef, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import {
  ArrowLeft,
  BarChart3,
  Database,
  LayoutDashboard,
  Pencil,
  Plus,
  Send,
  SlidersHorizontal,
  Sparkles,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { MarkdownView } from "@/lib/markdown";

import {
  streamAgentChat,
  useAiModels,
  usePreviewQuery,
  useReport,
  useReportMessages,
  useUpdateReport,
  type AgentMessage,
  type FilterDef,
  type PanelProposal,
  type ReportPanel,
  type ToolResultMeta,
} from "../api";
import { FilterDefsForm } from "../components/FilterDefsForm";
import { PanelCard } from "../components/PanelCard";
import { PanelEditor } from "../components/PanelEditor";
import { PanelView } from "../components/PanelView";
import { TextPanelForm } from "../components/TextPanelForm";
import { columnsFromSql, detectParams, type AgentQueryResult } from "../viz";

/**
 * The conversational report builder (builder == editor). The user describes the
 * data they want; the agent runs read-only SQL (shown transparently) and settles
 * on a query. Chart modeling + dashboard composition arrive in later phases.
 */
export function ReportBuilderPage() {
  const { reportId } = useParams({ strict: false }) as { reportId?: string };
  const report = useReport(reportId);
  const canEdit = report.data?.can_edit ?? false;

  // The thread + model picker are edit-only surfaces (their endpoints require
  // reports.write), so only fetch them once we know the caller can edit.
  const messages = useReportMessages(canEdit ? reportId : undefined);
  const models = useAiModels(canEdit);
  const qc = useQueryClient();

  const [input, setInput] = useState("");
  const [modelId, setModelId] = useState<string | null>(null);
  const [streaming, setStreaming] = useState(false);
  // The saved panel currently loaded into the editor for revision. `baselineLen`
  // is the thread length when editing began, so a query the agent runs *after*
  // (a conversational data change) swaps the editor onto that fresh result.
  const [editing, setEditing] = useState<{ panel: ReportPanel; baselineLen: number } | null>(null);
  const threadRef = useRef<HTMLDivElement>(null);

  // Adopt the server's default model once the picker loads.
  useEffect(() => {
    if (modelId === null && models.data?.default_id) setModelId(models.data.default_id);
  }, [modelId, models.data?.default_id]);

  const thread = useMemo(() => messages.data ?? [], [messages.data]);
  useEffect(() => {
    threadRef.current?.scrollTo({ top: threadRef.current.scrollHeight });
  }, [thread.length, streaming]);

  const hasModels = (models.data?.models.length ?? 0) > 0;

  // Index of the most recent successful query result in the thread (-1 if none).
  const latestResultAt = useMemo(() => {
    for (let i = thread.length - 1; i >= 0; i -= 1) {
      const m = thread[i];
      const meta = m?.tool_result_meta;
      if (m?.role === "tool" && meta?.ok && meta.columns && meta.columns.length > 0) return i;
    }
    return -1;
  }, [thread]);

  // The most recent successful query the agent ran — the builder models it.
  const latestResult = useMemo<AgentQueryResult | null>(() => {
    if (latestResultAt < 0) return null;
    const meta = thread[latestResultAt]?.tool_result_meta;
    if (!meta) return null;
    return {
      columns: meta.columns ?? [],
      rows: (meta.rows as Record<string, unknown>[]) ?? [],
      sql: meta.sql,
      source: meta.source,
    };
  }, [thread, latestResultAt]);

  // While editing, a query the agent ran *after* editing began (a conversational
  // data change) becomes the editor's result, replacing the panel's own data.
  const hasNewQuery = !!editing && latestResultAt >= editing.baselineLen && !!latestResult;

  const update = useUpdateReport();
  const panels = useMemo(() => report.data?.config.panels ?? [], [report.data?.config.panels]);

  const savePanel = async (panel: ReportPanel) => {
    if (!reportId || !report.data) return;
    const next = { ...report.data.config, panels: [...report.data.config.panels, panel] };
    try {
      await update.mutateAsync({ id: reportId, input: { config: next } });
      toast.success("Panel added to report");
    } catch (e) {
      toast.error((e as Error)?.message ?? "Couldn’t save the panel");
    }
  };

  // Load a saved panel into the editor. For query panels, seed the composer with
  // an edit prompt (the agent can revise the query); text panels edit directly.
  const startEdit = (panel: ReportPanel) => {
    setEditing({ panel, baselineLen: thread.length });
    setInput(panel.query?.sql ? editPromptFor(panel) : "");
    threadRef.current?.scrollTo({ top: threadRef.current.scrollHeight });
  };
  const cancelEdit = () => {
    setEditing(null);
    setInput("");
  };

  // Save the edited panel in place; if it isn't in the report yet (an agent
  // proposal opened in the editor), append it.
  const updatePanel = async (panel: ReportPanel) => {
    if (!reportId || !report.data || !editing) return;
    const existing = report.data.config.panels;
    const found = existing.some((p) => p.id === editing.panel.id);
    const panels = found
      ? existing.map((p) =>
          p.id === editing.panel.id ? { ...panel, id: p.id, layout: p.layout } : p,
        )
      : [...existing, { ...panel, id: editing.panel.id }];
    try {
      await update.mutateAsync({ id: reportId, input: { config: { ...report.data.config, panels } } });
      toast.success(found ? "Panel updated" : "Panel added to report");
      setEditing(null);
      setInput("");
    } catch (e) {
      toast.error((e as Error)?.message ?? "Couldn’t save the panel");
    }
  };

  // The agent recommends panels (propose_panel); the user accepts them here.
  const proposalToPanel = (proposal: PanelProposal): ReportPanel => ({
    id: crypto.randomUUID(),
    type: proposal.type,
    title: proposal.title,
    layout: { x: 0, y: 0, w: 6, h: 8 },
    query: proposal.query,
    viz: proposal.viz,
  });
  const addProposal = (proposal: PanelProposal) => void savePanel(proposalToPanel(proposal));
  const editProposal = (proposal: PanelProposal) =>
    setEditing({ panel: proposalToPanel(proposal), baselineLen: thread.length });

  const removePanel = async (panelId: string) => {
    if (!reportId || !report.data) return;
    const next = {
      ...report.data.config,
      panels: report.data.config.panels.filter((p) => p.id !== panelId),
    };
    try {
      await update.mutateAsync({ id: reportId, input: { config: next } });
    } catch (e) {
      toast.error((e as Error)?.message ?? "Couldn’t remove the panel");
    }
  };

  // Filter params referenced ({{name}}) across all saved panels' SQL.
  const filterParams = useMemo(
    () => detectParams(panels.map((p) => p.query?.sql)),
    [panels],
  );
  // Result columns available to target with a column auto-filter — from each
  // panel's viz mappings plus a best-effort parse of its SELECT list.
  const availableColumns = useMemo(() => {
    const set = new Set<string>();
    for (const p of panels) {
      const v = p.viz ?? {};
      for (const c of [v.xKey, v.valueKey, v.splitBy]) if (c) set.add(c);
      for (const s of v.series ?? []) if (s.key) set.add(s.key);
      for (const c of v.columns ?? []) set.add(c);
      for (const c of columnsFromSql(p.query?.sql)) set.add(c);
    }
    return [...set].sort();
  }, [panels]);
  const saveFilters = async (filters: FilterDef[]) => {
    if (!reportId || !report.data) return;
    try {
      await update.mutateAsync({ id: reportId, input: { config: { ...report.data.config, filters } } });
      toast.success("Filters saved");
    } catch (e) {
      toast.error((e as Error)?.message ?? "Couldn’t save filters");
    }
  };

  const send = async () => {
    const content = input.trim();
    if (!content || !reportId) return;
    setInput("");
    setStreaming(true);
    try {
      // Stream the turn: each persisted message is appended to the thread cache
      // as it arrives, so the agent's tool-runs show up live.
      await streamAgentChat(reportId, content, modelId, (m) => {
        qc.setQueryData<AgentMessage[]>(["reports", "messages", reportId], (old) => {
          const list = old ?? [];
          return list.some((x) => x.id === m.id) ? list : [...list, m];
        });
      });
    } catch (e) {
      toast.error((e as Error)?.message ?? "The agent turn failed");
      setInput(content); // restore so the user doesn't lose their message
    } finally {
      setStreaming(false);
      // Reconcile against the authoritative persisted thread.
      void qc.invalidateQueries({ queryKey: ["reports", "messages", reportId] });
    }
  };

  return (
    <div className="mx-auto flex h-[calc(100vh-6rem)] max-w-[95rem] flex-col gap-3 pb-2">
      <header className="flex flex-wrap items-center gap-3">
        <Link to="/reports" className="text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div className="min-w-0 flex-1">
          <h1 className="flex items-center gap-2 truncate text-xl font-bold tracking-tight">
            <Sparkles className="h-5 w-5 text-brand" />
            {report.data?.title ?? "Report"}
          </h1>
          <p className="text-xs text-muted-foreground">
            Describe the data you want — the agent writes and runs read-only SQL, and
            shows you exactly what it ran.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {reportId && (
            <Button variant="outline" size="sm" asChild>
              <Link to="/reports/$reportId/view" params={{ reportId }}>
                <LayoutDashboard className="h-4 w-4" />
                Dashboard
              </Link>
            </Button>
          )}
          <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Model
          </span>
          <Select value={modelId ?? ""} onValueChange={(v) => setModelId(v)} disabled={!hasModels}>
            <SelectTrigger className="w-56">
              <SelectValue placeholder={hasModels ? "Pick a model" : "No models available"} />
            </SelectTrigger>
            <SelectContent>
              {(models.data?.models ?? []).map((m) => (
                <SelectItem key={m.id} value={m.id}>
                  {m.name} · {m.provider}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </header>

      {report.isError ? (
        <Alert variant="danger">
          <AlertTitle>Report not found</AlertTitle>
          <AlertDescription>
            {(report.error as Error)?.message ?? "You may not have access to this report."}
          </AlertDescription>
        </Alert>
      ) : report.data && !canEdit ? (
        <Alert variant="info">
          <AlertTitle>View-only access</AlertTitle>
          <AlertDescription>
            You can view this report but not edit it. Building requires edit access.
          </AlertDescription>
        </Alert>
      ) : (
        <>
          {!hasModels && !models.isLoading && (
            <Alert variant="warning">
              <AlertTitle>No AI models available</AlertTitle>
              <AlertDescription>
                Start the local Ollama model, or a superuser can add a Claude API key
                under Settings → AI.
              </AlertDescription>
            </Alert>
          )}

          <div className="grid min-h-0 flex-1 gap-3 lg:grid-cols-2">
            {/* ── Left: the conversation ───────────────────────────── */}
            <div className="flex min-h-0 flex-col gap-3">
              <div
                ref={threadRef}
                className="min-h-0 flex-1 space-y-3 overflow-y-auto rounded-lg border border-border bg-card p-4"
              >
                {messages.isLoading ? (
                  <Skeleton className="h-24 w-full" />
                ) : thread.length === 0 ? (
                  <div className="flex h-full flex-col items-center justify-center gap-2 text-center text-sm text-muted-foreground">
                    <Database className="h-8 w-8" />
                    <p className="max-w-md">
                      Start by describing the data you want — e.g.{" "}
                      <em>“monthly ARR for the last 12 months”</em> or{" "}
                      <em>“active contracts by product line”</em>.
                    </p>
                  </div>
                ) : (
                  thread.map((m) => (
                    <MessageRow
                      key={m.id}
                      message={m}
                      onAddProposal={addProposal}
                      onEditProposal={editProposal}
                    />
                  ))
                )}
                {streaming && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Sparkles className="h-4 w-4 animate-pulse text-brand" />
                    Agent is working…
                  </div>
                )}
              </div>

              {/* Composer */}
              <div className="flex items-end gap-2">
                <Textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      void send();
                    }
                  }}
                  placeholder="Describe the data you want…"
                  rows={2}
                  className="resize-none"
                  disabled={streaming || !hasModels}
                />
                <Button
                  onClick={() => void send()}
                  disabled={streaming || !input.trim() || !hasModels}
                >
                  <Send className="h-4 w-4" />
                  Send
                </Button>
              </div>
            </div>

            {/* ── Right: model the query into panels + saved report ── */}
            <div className="hidden min-h-0 flex-col rounded-lg border border-border bg-card lg:flex">
              <div className="flex items-center gap-2 border-b border-border px-4 py-2">
                <BarChart3 className="h-4 w-4 text-brand" />
                <span className="text-sm font-semibold">Model &amp; panels</span>
                {panels.length > 0 && (
                  <span className="ml-auto text-xs text-muted-foreground">
                    {panels.length} panel{panels.length === 1 ? "" : "s"} saved
                  </span>
                )}
              </div>
              <div className="min-h-0 flex-1 space-y-5 overflow-auto p-4">
                {editing ? (
                  editing.panel.type === "text" ? (
                    <TextPanelForm
                      key={`edit-text-${editing.panel.id}`}
                      editing
                      initialTitle={editing.panel.title ?? ""}
                      initialContent={editing.panel.viz.content ?? ""}
                      onAdd={(p) => void updatePanel(p)}
                      onCancel={cancelEdit}
                      saving={update.isPending}
                    />
                  ) : (
                    <EditPanelSection
                      panel={editing.panel}
                      overrideResult={hasNewQuery ? latestResult : null}
                      keepVizFromPanel={!hasNewQuery}
                      onSave={(p) => void updatePanel(p)}
                      onCancel={cancelEdit}
                      saving={update.isPending}
                    />
                  )
                ) : latestResult ? (
                  <PanelEditor
                    key={latestResult.sql ?? "result"}
                    result={latestResult}
                    onAdd={(p) => void savePanel(p)}
                    saving={update.isPending}
                  />
                ) : (
                  <div className="flex h-40 flex-col items-center justify-center gap-2 text-center text-sm text-muted-foreground">
                    <BarChart3 className="h-8 w-8" />
                    <p className="max-w-xs">
                      When the agent runs a query, model it into a chart here and add it to
                      the report.
                    </p>
                  </div>
                )}

                <div className="space-y-2 border-t border-border pt-4">
                  <TextPanelForm onAdd={(p) => void savePanel(p)} saving={update.isPending} />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setInput(filterPrompt())}
                    title="Ask the agent to add a dashboard filter"
                  >
                    <SlidersHorizontal className="h-3.5 w-3.5" />
                    Add a filter
                  </Button>
                  <FilterDefsForm
                    key={[...filterParams, ...(report.data?.config.filters ?? []).map((d) => d.param)].join(",")}
                    params={filterParams}
                    defs={report.data?.config.filters ?? []}
                    columns={availableColumns}
                    onSave={(f) => void saveFilters(f)}
                    saving={update.isPending}
                  />
                </div>

                {panels.length > 0 && (
                  <div className="space-y-3">
                    <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Saved panels
                    </div>
                    {panels.map((p) => (
                      <PanelCard
                        key={p.id}
                        panel={p}
                        active={editing?.panel.id === p.id}
                        onEdit={() => startEdit(p)}
                        onRemove={() => void removePanel(p.id)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function MessageRow({
  message,
  onAddProposal,
  onEditProposal,
}: {
  message: AgentMessage;
  onAddProposal: (p: PanelProposal) => void;
  onEditProposal: (p: PanelProposal) => void;
}) {
  if (message.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="max-w-[80%] whitespace-pre-wrap rounded-lg bg-brand px-3 py-2 text-sm text-white">
          {message.content}
        </div>
      </div>
    );
  }
  if (message.role === "tool") {
    const meta = message.tool_result_meta;
    if (!meta) return null;
    if (meta.proposal) {
      return <ProposalCard proposal={meta.proposal} onAdd={onAddProposal} onEdit={onEditProposal} />;
    }
    return <ToolResultCard meta={meta} />;
  }
  // assistant — content may be empty when the turn was purely tool calls.
  if (!message.content?.trim()) return null;
  return (
    <div className="flex justify-start">
      <div className="max-w-[85%] rounded-lg border border-border bg-background px-3 py-2">
        <MarkdownView source={message.content} />
      </div>
    </div>
  );
}

/** The agent's panel recommendation (propose_panel) — a live preview + accept. */
function ProposalCard({
  proposal,
  onAdd,
  onEdit,
}: {
  proposal: PanelProposal;
  onAdd: (p: PanelProposal) => void;
  onEdit: (p: PanelProposal) => void;
}) {
  const panel: ReportPanel = {
    id: "proposal-preview",
    type: proposal.type,
    title: proposal.title,
    layout: { x: 0, y: 0, w: 6, h: 8 },
    query: proposal.query,
    viz: proposal.viz,
  };
  return (
    <div className="rounded-md border border-brand/40 bg-brand/5 p-2">
      <div className="mb-1.5 flex items-center gap-2 text-xs">
        <Sparkles className="h-3.5 w-3.5 shrink-0 text-brand" />
        <span className="min-w-0 flex-1 truncate font-semibold">
          Recommended: {proposal.title || "Untitled panel"}
        </span>
        <Badge variant="secondary">{proposal.type}</Badge>
      </div>
      {/* Preview only — pointer-events-none so the chart canvas can't intercept
          clicks meant for the Add / Open buttons below it. */}
      <div className="pointer-events-none rounded border border-border bg-card p-2">
        <PanelView panel={panel} className="h-44" />
      </div>
      <div className="mt-2 flex gap-2">
        <Button size="sm" onClick={() => onAdd(proposal)}>
          <Plus className="h-3.5 w-3.5" />
          Add to report
        </Button>
        <Button variant="outline" size="sm" onClick={() => onEdit(proposal)}>
          <Pencil className="h-3.5 w-3.5" />
          Open in editor
        </Button>
      </div>
    </div>
  );
}

function ToolResultCard({ meta }: { meta: ToolResultMeta }) {
  const [showSql, setShowSql] = useState(false);
  const ok = meta.ok !== false;
  const columns = meta.columns ?? [];
  const rows = meta.rows ?? [];
  return (
    <div className="rounded-md border border-border bg-muted/30 p-2 text-xs">
      <div className="flex items-center gap-2">
        <Database className="h-3.5 w-3.5 text-power" />
        <span className="font-semibold">Ran SQL · {meta.source ?? "?"}</span>
        {ok ? (
          <Badge variant="success">
            {meta.row_count ?? 0} row{(meta.row_count ?? 0) === 1 ? "" : "s"}
            {meta.truncated ? "+" : ""}
          </Badge>
        ) : (
          <Badge variant="danger">error</Badge>
        )}
        {meta.sql && (
          <button
            type="button"
            className="ml-auto font-medium text-brand hover:underline"
            onClick={() => setShowSql((v) => !v)}
          >
            {showSql ? "Hide" : "Show"} SQL
          </button>
        )}
      </div>
      {showSql && meta.sql && (
        <pre className="mt-2 overflow-x-auto rounded bg-background p-2 text-[11px] leading-relaxed">
          {meta.sql}
        </pre>
      )}
      {!ok && meta.error && <p className="mt-1 text-danger">{meta.error}</p>}
      {ok && columns.length > 0 && (
        <div className="mt-2 max-h-64 overflow-auto rounded-md border border-border">
          <table className="w-full text-[11px]">
            <thead className="sticky top-0 bg-muted">
              <tr>
                {columns.map((c) => (
                  <th key={c} className="whitespace-nowrap px-2 py-1 text-left font-semibold">
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 50).map((r, i) => (
                <tr key={i} className="border-t border-border">
                  {columns.map((c) => (
                    <td key={c} className="whitespace-nowrap px-2 py-1 tabular-nums">
                      {fmtCell(r[c])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function fmtCell(value: unknown): string {
  if (value == null) return "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

/** Seed the composer with a prompt asking the agent to add a dashboard filter —
 *  the discoverable path to the {{param}} placeholder mechanism. */
function filterPrompt(): string {
  return (
    "Add a dashboard filter. I'd like to filter by [which column — e.g. a date range, " +
    "customer segment, or product]. Add a {{parameter}} placeholder to the relevant " +
    "panel's query so a filter control appears on the dashboard, and tell me what you changed."
  );
}

/** Seed the composer with an edit prompt referencing a panel + its current SQL,
 *  so a data/query change can be described conversationally. */
function editPromptFor(panel: ReportPanel): string {
  const sql = panel.query?.sql?.trim();
  const name = panel.title ? `"${panel.title}"` : "this";
  return (
    `I want to edit the ${name} panel.\n` +
    (sql ? `Its current query is:\n\`\`\`sql\n${sql}\n\`\`\`\n` : "") +
    `\nWhat I'd like to change: `
  );
}

/**
 * The panel editor in "revise an existing panel" mode. Runs the panel's own
 * saved query for a live preview and pre-fills the controls from its spec —
 * unless a fresh agent query (a conversational data change) has superseded it,
 * in which case that result drives the editor. Saving updates the panel in place.
 */
function EditPanelSection({
  panel,
  overrideResult,
  keepVizFromPanel,
  onSave,
  onCancel,
  saving,
}: {
  panel: ReportPanel;
  /** A newer agent query result to model instead of the panel's own data. */
  overrideResult: AgentQueryResult | null;
  /** Seed the editor from the panel's viz (false once a new query supersedes it). */
  keepVizFromPanel: boolean;
  onSave: (panel: ReportPanel) => void;
  onCancel: () => void;
  saving?: boolean;
}) {
  // Fetch the panel's own data only while no fresh query has superseded it.
  const preview = usePreviewQuery(
    panel.query?.source,
    panel.query?.sql,
    undefined,
    !overrideResult && !!panel.query?.sql,
  );
  const ownResult: AgentQueryResult | null = preview.data
    ? {
        columns: preview.data.columns,
        rows: preview.data.rows,
        sql: panel.query?.sql,
        source: panel.query?.source,
      }
    : null;
  const result = overrideResult ?? ownResult;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 rounded-md border border-brand/40 bg-brand/5 px-3 py-2">
        <Pencil className="h-4 w-4 shrink-0 text-brand" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">
            Editing: {panel.title || "Untitled panel"}
          </p>
          <p className="text-[11px] text-muted-foreground">
            {overrideResult
              ? "Modeling the revised query — adjust and Save changes."
              : "Tweak the chart, or describe a data change in the chat to revise the query."}
          </p>
        </div>
        <Button variant="ghost" size="sm" className="h-7 shrink-0" onClick={onCancel}>
          <X className="h-3.5 w-3.5" />
          Cancel
        </Button>
      </div>

      {preview.isLoading && !overrideResult ? (
        <Skeleton className="h-64 w-full" />
      ) : preview.isError && !overrideResult ? (
        <p className="py-6 text-center text-xs text-danger">
          {(preview.error as Error)?.message ?? "Couldn’t load this panel’s data to edit."}
        </p>
      ) : result ? (
        <PanelEditor
          key={`edit-${panel.id}-${result.sql ?? ""}`}
          result={result}
          editing
          initialTitle={panel.title}
          initialViz={keepVizFromPanel ? panel.viz : undefined}
          initialMode={panel.type === "grid" ? "table" : panel.type === "stat" ? "stat" : "chart"}
          onAdd={onSave}
          saving={saving}
        />
      ) : null}
    </div>
  );
}
